import { useState, useEffect, useMemo, useCallback } from 'react';
import { collection, getDocs, deleteDoc, doc, query, orderBy, limit, startAfter, DocumentSnapshot } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { FinanceReport } from '../../types/financeReport';
import { Plus, Eye, Trash2, Banknote, ChevronLeft, ChevronRight, Pencil } from 'lucide-react';
import { ReportFilterBar, ReportFilters, defaultFilters, isFiltersActive, applyFilters } from '../ui/ReportFilterBar';
import { MeetingTypeSelect } from '../ui/MeetingTypeSelect';
import { X } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { FinanceReportPreview } from './FinanceReportPreview';
import FinanceReportForm from './FinanceReportForm';
import EditFinanceReportForm from './EditFinanceReportForm';
import { canEditReport, canDeleteReport } from '../../utils/permissions';
import { FinanceTrendChart } from './FinanceTrendChart';
import { formatNumber } from '../../utils/format';
import { useConfirm } from '../../contexts/ConfirmContext';

export default function FinanceReportsList() {
  const confirm = useConfirm();
  const { user } = useAuth();
  const [reports, setReports] = useState<FinanceReport[]>([]);
  const [allReports, setAllReports] = useState<FinanceReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState<ReportFilters>(defaultFilters());
  const [showForm, setShowForm] = useState(false);
  const [selectedReport, setSelectedReport] = useState<FinanceReport | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [editingReport, setEditingReport] = useState<FinanceReport | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [lastDoc, setLastDoc] = useState<DocumentSnapshot | null>(null);
  const [_firstDoc, setFirstDoc] = useState<DocumentSnapshot | null>(null);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [hasPrevPage, setHasPrevPage] = useState(false);
  const [statsRange, setStatsRange] = useState<{ from: Date | null; to: Date | null; label: string }>({ from: null, to: null, label: "Tout l'historique" });
  const reportsPerPage = 10;

  const handleRangeChange = useCallback(
    (range: { from: Date | null; to: Date | null; label: string }) => {
      setStatsRange(range);
    },
    []
  );

  const [meetingTypesMap, setMeetingTypesMap] = useState<Record<string, string>>({});

  useEffect(() => {
    getDocs(collection(db, 'meetingTypes')).then(snap => {
      const map: Record<string, string> = {};
      snap.forEach(d => { map[d.id] = (d.data() as { name?: string }).name ?? d.id; });
      setMeetingTypesMap(map);
    });
  }, []);

  const loadReports = async (page = 1, searchAfterDoc?: DocumentSnapshot | null) => {
    try {
      setLoading(true);
      const reportsRef = collection(db, 'financeReports');
      
      let q = query(reportsRef, orderBy('date', 'desc'), limit(reportsPerPage + 1));
      
      if (page > 1 && searchAfterDoc) {
        q = query(reportsRef, orderBy('date', 'desc'), startAfter(searchAfterDoc), limit(reportsPerPage + 1));
      }
      
      const snapshot = await getDocs(q);
      const reportsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as FinanceReport[];
      
      // Check if there are more reports for next page
      const hasMore = reportsData.length > reportsPerPage;
      if (hasMore) {
        reportsData.pop(); // Remove the extra document used for pagination check
      }
      
      setReports(reportsData);
      setHasNextPage(hasMore);
      setHasPrevPage(page > 1);
      
      // Set pagination documents
      if (reportsData.length > 0) {
        setFirstDoc(snapshot.docs[0]);
        setLastDoc(snapshot.docs[reportsData.length - 1]);
      }
      
      // Load all reports for statistics (only on first load)
      if (page === 1 && !searchAfterDoc) {
        const allReportsQuery = query(reportsRef, orderBy('date', 'desc'));
        const allSnapshot = await getDocs(allReportsQuery);
        const allReportsData = allSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as FinanceReport[];
        setAllReports(allReportsData);
      }
    } catch (err) {
      console.error('Erreur lors du chargement des rapports:', err);
      setError('Erreur lors du chargement des rapports');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReports();
  }, []);

  const goToNextPage = () => {
    if (hasNextPage && lastDoc) {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      loadReports(nextPage, lastDoc);
    }
  };

  const goToPrevPage = () => {
    if (hasPrevPage && currentPage > 1) {
      const prevPage = currentPage - 1;
      setCurrentPage(prevPage);
      // For previous page, we need to reload from the beginning
      // This is a limitation of Firestore pagination
      if (prevPage === 1) {
        loadReports(1);
      } else {
        // For now, we'll just reload from the beginning and skip to the right page
        // This is not the most efficient but works for small datasets
        loadReports(1);
        setCurrentPage(prevPage);
      }
    }
  };
  const handleDelete = async (id: string) => {
    const ok = await confirm({ title: 'Supprimer le rapport', description: 'Êtes-vous sûr de vouloir supprimer ce rapport financier ?', confirmLabel: 'Supprimer', cancelLabel: 'Annuler', variant: 'danger' });
    if (!ok) return;
    
    try {
      await deleteDoc(doc(db, 'financeReports', id));
      // Reload the current page after deletion
      loadReports(currentPage, currentPage > 1 ? lastDoc : null);
    } catch (err) {
      console.error('Erreur lors de la suppression:', err);
      setError('Erreur lors de la suppression du rapport');
    }
  };

  const handleFormSubmit = () => {
    setShowForm(false);
    // Go back to first page after adding new report
    setCurrentPage(1);
    loadReports(1);
  };

  const handlePreview = (report: FinanceReport) => {
    setSelectedReport(report);
    setShowPreview(true);
  };

  const handleEdit = (report: FinanceReport) => {
    setEditingReport(report);
  };

  const handleUpdateReport = () => {
    setEditingReport(null);
    // Reload the current page after update
    loadReports(currentPage, currentPage > 1 ? lastDoc : null);
  };

  const filtersActive = isFiltersActive(filters);
  const filteredReports = filtersActive
    ? applyFilters(
        allReports,
        filters,
        r => r.date,
        r => [new Date(r.date).toLocaleDateString('fr-FR'), r.notes ?? ''],
        r => r.meetingTypeId
      )
    : reports;

  // Use all reports for statistics, filtered by the chart's selected period
  const baseStatsReports = allReports.length > 0 ? allReports : reports;
  const statsReports = useMemo(() => {
    const { from, to } = statsRange;
    if (!from && !to) return baseStatsReports;
    return baseStatsReports.filter((r) => {
      const d = new Date(r.date);
      if (from && d < from) return false;
      if (to && d > to) return false;
      return true;
    });
  }, [baseStatsReports, statsRange]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-[#00665C]"></div>
      </div>
    );
  }


  return (

    <>
      {/* ===== MOBILE VIEW (< md) ===== */}
      <div className="md:hidden">
        <div className="min-h-screen bg-gray-50">
      {showForm && (
        <FinanceReportForm onSubmit={handleFormSubmit} onClose={() => setShowForm(false)} />
      )}
      {showPreview && selectedReport && (
        <FinanceReportPreview report={selectedReport} onClose={() => { setShowPreview(false); setSelectedReport(null); }} />
      )}
      {editingReport && (
        <EditFinanceReportForm report={editingReport} onClose={() => setEditingReport(null)} />
      )}
      {!showForm && !showPreview && !editingReport && (
        <>
          <div className="bg-vh-green text-white px-4 pt-10 pb-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                  <Banknote className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-bold tracking-tight">Finances</h1>
                  <p className="text-white/70 text-sm">Gestion des rapports</p>
                </div>
              </div>
              <button onClick={() => setShowForm(true)} className="w-12 h-12 bg-vh-gold rounded-full flex items-center justify-center shadow-lg active:scale-95 transition-transform">
                <Plus className="w-6 h-6 text-white" />
              </button>
            </div>
            <div className="bg-white/10 rounded-xl p-3 flex items-center justify-center gap-2">
              <span className="text-2xl font-bold">{filteredReports.length}</span>
              <span className="text-white/70 text-sm">rapport(s) affichés</span>
            </div>
          </div>
          <div className="sticky top-0 z-10 bg-white shadow-sm px-4 py-2">
            <ReportFilterBar filters={filters} onChange={setFilters} />
          </div>
          {loading && <div className="flex justify-center items-center py-16"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-vh-green" /></div>}
          {error && <div className="mx-4 mt-4 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">{error}</div>}
          {!loading && (
            <div className="px-4 py-4 space-y-3">
              {filteredReports.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                  <Banknote className="w-12 h-12 mb-3 text-gray-300" />
                  <p className="text-base font-medium">Aucun rapport trouvé</p>
                  <p className="text-sm mt-1">Appuyez sur + pour créer un rapport</p>
                </div>
              ) : (
                filteredReports.map(report => (
                  <div key={report.id} className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                    <div className="flex items-stretch">
                      <div className="w-14 bg-vh-green flex flex-col items-center justify-center py-3 flex-shrink-0">
                        <span className="text-white font-bold text-xl leading-none">
                          {report.date?.toDate ? report.date.toDate().getDate() : new Date(report.date ?? 0).getDate()}
                        </span>
                        <span className="text-white/80 text-xs mt-0.5">
                          {report.date?.toDate ? report.date.toDate().toLocaleDateString('fr-FR', { month: 'short' }) : new Date(report.date ?? 0).toLocaleDateString('fr-FR', { month: 'short' })}
                        </span>
                      </div>
                      <div className="flex-1 px-3 py-2.5 min-w-0">
                        <p className="font-semibold text-gray-800 text-sm truncate">{meetingTypesMap[report.meetingTypeId] ?? report.meetingTypeId}</p>
                        <p className="text-vh-green font-bold text-base mt-1">
                          {typeof report.totalFinances === 'number' ? report.totalFinances.toLocaleString('fr-FR') : '--'} FCFA
                        </p>
                      </div>
                      <div className="flex flex-col border-l border-gray-100 flex-shrink-0">
                        <button onClick={() => handlePreview(report)} className="flex-1 flex items-center justify-center px-3 text-blue-500 hover:bg-blue-50 transition-colors">
                          <Eye className="w-4 h-4" />
                        </button>
                        {canEditReport(report, user) && (
                          <>
                            <div className="border-t border-gray-100" />
                            <button onClick={() => handleEdit(report)} className="flex-1 flex items-center justify-center px-3 text-amber-500 hover:bg-amber-50 transition-colors">
                              <Pencil className="w-4 h-4" />
                            </button>
                          </>
                        )}
                        {canDeleteReport(report, user) && (
                          <>
                            <div className="border-t border-gray-100" />
                            <button onClick={() => handleDelete(report.id)} className="flex-1 flex items-center justify-center px-3 text-red-400 hover:bg-red-50 transition-colors">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
          {(hasPrevPage || hasNextPage) && (
            <div className="flex items-center justify-between px-4 pb-6 pt-2">
              <button onClick={goToPrevPage} disabled={!hasPrevPage} className={hasPrevPage ? 'flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-medium bg-vh-green text-white' : 'flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-medium bg-gray-100 text-gray-400 cursor-not-allowed'}>
                <ChevronLeft className="w-4 h-4" /> Précédent
              </button>
              <span className="text-sm text-gray-500 font-medium">Page {currentPage}</span>
              <button onClick={goToNextPage} disabled={!hasNextPage} className={hasNextPage ? 'flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-medium bg-vh-green text-white' : 'flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-medium bg-gray-100 text-gray-400 cursor-not-allowed'}>
                Suivant <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </>
      )}
    </div>
      </div>

      {/* ===== DESKTOP VIEW (>= md) ===== */}
      <div className="hidden md:block">
        <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex justify-between items-start mb-8">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <Banknote className="h-8 w-8 text-[#00665C]" />
              <h1 className="text-3xl font-bold text-gray-900">Finance</h1>
            </div>
            <p className="text-gray-600">
              Gérez les rapports financiers des cultes
            </p>
          </div>
          <button
            onClick={() => setShowForm(!showForm)}
            className={`px-4 py-2 rounded-lg transition-colors flex items-center gap-2 ${
              showForm
                ? 'bg-gray-600 text-white hover:bg-gray-700'
                : 'bg-[#00665C] text-white hover:bg-[#004d45]'
            }`}
          >
            {showForm ? (
              <><X className="h-4 w-4" />Fermer le formulaire</>
            ) : (
              <><Plus className="h-4 w-4" />Nouveau rapport</>
            )}
          </button>
        </div>

        {/* Statistiques rapides */}
        {baseStatsReports.length > 0 && (
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
              <h2 className="text-sm font-medium text-gray-700">Statistiques</h2>
              <span className="text-xs text-[#00665C] bg-[#00665C]/8 px-2 py-1 rounded-full">
                Période : {statsRange.label}
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-white p-4 rounded-lg shadow-sm" style={{ borderLeft: '4px solid #00665C' }}>
                <h3 className="text-sm font-medium text-gray-500">Total Rapports</h3>
                <p className="text-2xl font-bold text-[#00665C]">{statsReports.length}</p>
              </div>
              <div className="bg-white p-4 rounded-lg shadow-sm" style={{ borderLeft: '4px solid #10B981' }}>
                <h3 className="text-sm font-medium text-gray-500">Total Dîmes</h3>
                <p className="text-2xl font-bold text-green-600">
                  {formatNumber(statsReports.reduce((sum, r) => sum + r.finances.tithes, 0))} FCFA
                </p>
              </div>
              <div className="bg-white p-4 rounded-lg shadow-sm" style={{ borderLeft: '4px solid #3B82F6' }}>
                <h3 className="text-sm font-medium text-gray-500">Total Offrandes</h3>
                <p className="text-2xl font-bold text-blue-600">
                  {formatNumber(statsReports.reduce((sum, r) => sum + r.finances.regularOfferings + r.finances.specialOfferings, 0))} FCFA
                </p>
              </div>
              <div className="bg-white p-4 rounded-lg shadow-sm" style={{ borderLeft: '4px solid #8B5CF6' }}>
                <h3 className="text-sm font-medium text-gray-500">Total Général</h3>
                <p className="text-2xl font-bold text-[#00665C]">
                  {formatNumber(statsReports.reduce((sum, r) => sum + r.totalFinances, 0))} FCFA
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Graphique d'évolution */}
        {allReports.length > 0 && (
          <FinanceTrendChart reports={allReports} onRangeChange={handleRangeChange} />
        )}


        {/* Filtres */}
        <div className="mb-6">
          <ReportFilterBar
            filters={filters}
            onChange={setFilters}
            onReset={() => setFilters(defaultFilters())}
            placeholder="Rechercher par date ou notes…"
            resultCount={filteredReports.length}
            totalCount={allReports.length}
            meetingTypeSlot={
              <MeetingTypeSelect
                value={filters.meetingTypeId ?? ''}
                onChange={v => setFilters(f => ({ ...f, meetingTypeId: v }))}
                required={false}
              />
            }
          />
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        {/* Formulaire inline pour nouveau rapport */}
        {showForm && (
          <div className="mb-6">
            <FinanceReportForm
              onSubmit={handleFormSubmit}
              onClose={() => setShowForm(false)}
            />
          </div>
        )}

        {/* Liste des rapports */}
        {filteredReports.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <Banknote className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun rapport trouvé</h3>
            <p className="text-gray-600 mb-6">
              {filtersActive ? 'Aucun rapport ne correspond à vos filtres.' : 'Commencez par créer votre premier rapport financier.'}
            </p>
            {!filtersActive && (
              <button
                onClick={() => setShowForm(true)}
                className="bg-[#00665C] text-white px-4 py-2 rounded-lg hover:bg-[#004d45] transition-colors inline-flex items-center gap-2"
              >
                <Plus className="h-4 w-4" />
                Créer un rapport
              </button>
            )}
          </div>
        ) : (
          <>
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            {/* Version mobile */}
            <div className="sm:hidden">
              {filteredReports.map((report) => (
                <div key={report.id} className="border-b border-gray-100 p-4">
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="font-medium text-gray-900">
                      {new Date(report.date).toLocaleDateString('fr-FR')}
                    </h3>
                    <div className="flex gap-1">
                      <button
                        onClick={() => handlePreview(report)}
                        className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                        title="Prévisualiser"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                      {canEditReport(report, user) && (
                        <button
                          onClick={() => handleEdit(report)}
                          className="p-1 text-green-600 hover:bg-green-50 rounded"
                          title="Modifier"
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                      )}
                      {canDeleteReport(report, user) && (
                        <button
                          onClick={() => handleDelete(report.id!)}
                          className="p-1 text-red-600 hover:bg-red-50 rounded"
                          title="Supprimer"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="text-sm text-gray-600">
                    <div>Total: {formatNumber(report.totalFinances)} FCFA</div>
                    <div className="grid grid-cols-2 gap-1 mt-1">
                      <div>Dîmes: {formatNumber(report.finances.tithes)}</div>
                      <div>Offrandes: {formatNumber(report.finances.regularOfferings + report.finances.specialOfferings)}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Version desktop */}
            <div className="hidden sm:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-100">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Dîmes
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Offrandes Ordinaires
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Offrandes Spéciales
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Total
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {filteredReports.map((report) => (
                    <tr key={report.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {new Date(report.date).toLocaleDateString('fr-FR')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {formatNumber(report.finances.tithes)} FCFA
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {formatNumber(report.finances.regularOfferings)} FCFA
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {formatNumber(report.finances.specialOfferings)} FCFA
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-[#00665C]">
                        {formatNumber(report.totalFinances)} FCFA
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => handlePreview(report)}
                            className="text-blue-600 hover:text-blue-900 p-1 hover:bg-blue-50 rounded"
                            title="Prévisualiser"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          {canEditReport(report, user) && (
                            <button
                              onClick={() => handleEdit(report)}
                              className="text-green-600 hover:text-green-900 p-1 hover:bg-green-50 rounded"
                              title="Modifier"
                            >
                              <Pencil className="h-4 w-4" />
                            </button>
                          )}
                          {canDeleteReport(report, user) && (
                            <button
                              onClick={() => handleDelete(report.id!)}
                              className="text-red-600 hover:text-red-900 p-1 hover:bg-red-50 rounded"
                              title="Supprimer"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          </>
        )}
        
        {/* Pagination */}
        {!filtersActive && filteredReports.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm p-4 mt-6">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-600">
                Page {currentPage} • {filteredReports.length} rapports affichés
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={goToPrevPage}
                  disabled={!hasPrevPage}
                  className={`flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                    hasPrevPage
                      ? 'text-[#00665C] bg-[#00665C]/8 hover:bg-[#00665C]/15'
                      : 'text-gray-400 bg-gray-100 cursor-not-allowed'
                  }`}
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Précédent
                </button>
                <div className="px-3 py-2 text-sm font-medium text-gray-700">
                  {currentPage}
                </div>
                <button
                  onClick={goToNextPage}
                  disabled={!hasNextPage}
                  className={`flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                    hasNextPage
                      ? 'text-[#00665C] bg-[#00665C]/8 hover:bg-[#00665C]/15'
                      : 'text-gray-400 bg-gray-100 cursor-not-allowed'
                  }`}
                >
                  Suivant
                  <ChevronRight className="h-4 w-4 ml-1" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {showPreview && selectedReport && (
        <FinanceReportPreview
          report={selectedReport}
          onClose={() => setShowPreview(false)}
        />
      )}

      {editingReport && (
        <EditFinanceReportForm
          report={editingReport}
          onClose={() => setEditingReport(null)}
          onUpdate={handleUpdateReport}
        />
      )}
    </div>
      </div>
    </>
  );
}
